/**
 * EB-1A Visa Criteria Definitions
 *
 * The EB-1A visa is for individuals with extraordinary ability in sciences, arts,
 * education, business, or athletics. The individual must demonstrate sustained
 * national or international acclaim.
 *
 * Legal Reference: 8 CFR 204.5(h)
 */

import { VisaCriterion } from './types';

/**
 * EB-1A visa criteria
 *
 * The beneficiary must meet at least 3 out of 10 criteria (or demonstrate
 * a one-time achievement such as a major internationally recognized award
 * like Nobel Prize, Oscar, Olympic Medal, etc.).
 */
export const EB1A_CRITERIA: VisaCriterion[] = [
  {
    id: 'eb1a_awards',
    name: 'Awards or Recognition',
    description: 'Receipt of lesser nationally or internationally recognized prizes or awards for excellence in the field of endeavor',
    weight: 9,
    requiredDocuments: [
      'AWARD_CERTIFICATE',
      'GRANT_APPROVAL',
      'COMPETITION_RESULTS'
    ],
    legalReference: '8 CFR 204.5(h)(3)(i)',
    minimumEvidence: 1
  },
  {
    id: 'eb1a_membership',
    name: 'Professional Memberships',
    description: 'Membership in associations in the field which require outstanding achievements of their members, as judged by recognized national or international experts in their disciplines or fields',
    weight: 8,
    requiredDocuments: [
      'MEMBERSHIP_CERTIFICATE'
    ],
    legalReference: '8 CFR 204.5(h)(3)(ii)',
    minimumEvidence: 1
  },
  {
    id: 'eb1a_published_material',
    name: 'Published Material About You',
    description: 'Published material about you in professional or major trade publications or other major media, relating to your work in the field. This must include the title, date, and author of the material, and any necessary translation',
    weight: 8,
    requiredDocuments: [
      'MEDIA_ARTICLE',
      'MEDIA_INTERVIEW'
    ],
    legalReference: '8 CFR 204.5(h)(3)(iii)',
    minimumEvidence: 3
  },
  {
    id: 'eb1a_judging',
    name: 'Judging Others',
    description: 'Participation, either individually or on a panel, as a judge of the work of others in the same or an allied field of specialization to that for which classification is sought',
    weight: 8,
    requiredDocuments: [
      'JUDGING_INVITATION',
      'PEER_REVIEW_CONFIRMATION'
    ],
    legalReference: '8 CFR 204.5(h)(3)(iv)',
    minimumEvidence: 2
  },
  {
    id: 'eb1a_contributions',
    name: 'Original Contributions',
    description: 'Evidence of original scientific, scholarly, artistic, athletic, or business-related contributions of major significance in the field',
    weight: 9,
    requiredDocuments: [
      'RESEARCH_PAPER',
      'PATENT_DOCUMENT',
      'INNOVATION_DESCRIPTION'
    ],
    legalReference: '8 CFR 204.5(h)(3)(v)',
    minimumEvidence: 2
  },
  {
    id: 'eb1a_scholarly_articles',
    name: 'Scholarly Articles',
    description: 'Authorship of scholarly articles in the field, in professional or major trade publications or other major media',
    weight: 8,
    requiredDocuments: [
      'PEER_REVIEWED_ARTICLE',
      'CONFERENCE_PROCEEDINGS'
    ],
    legalReference: '8 CFR 204.5(h)(3)(vi)',
    minimumEvidence: 3
  },
  {
    id: 'eb1a_exhibitions',
    name: 'Exhibitions or Showcases',
    description: 'Evidence of the display of the individual\'s work in the field at artistic exhibitions or showcases',
    weight: 7,
    requiredDocuments: [
      'EXHIBITION_CATALOGS',
      'EVENT_PHOTOS_VIDEOS'
    ],
    legalReference: '8 CFR 204.5(h)(3)(vii)',
    minimumEvidence: 2
  },
  {
    id: 'eb1a_leading_role',
    name: 'Leading or Critical Role',
    description: 'Evidence that the individual has performed in a leading or critical role for organizations or establishments that have a distinguished reputation',
    weight: 8,
    requiredDocuments: [
      'EMPLOYMENT_CONTRACT',
      'COMPANY_ORGANIZATIONAL_CHART',
      'CRITICAL_ROLE_DOCUMENT'
    ],
    legalReference: '8 CFR 204.5(h)(3)(viii)',
    minimumEvidence: 1
  },
  {
    id: 'eb1a_high_salary',
    name: 'High Salary or Remuneration',
    description: 'Evidence that the individual has commanded a high salary or other significantly high remuneration for services, in relation to others in the field',
    weight: 7,
    requiredDocuments: [
      'SALARY_STATEMENT',
      'TAX_RETURN',
      'EMPLOYMENT_CONTRACT'
    ],
    legalReference: '8 CFR 204.5(h)(3)(ix)',
    minimumEvidence: 2
  },
  {
    id: 'eb1a_commercial_success',
    name: 'Commercial Success',
    description: 'Evidence of commercial successes in the performing arts, as shown by box office receipts or record, cassette, compact disk, or video sales',
    weight: 7,
    requiredDocuments: [
      'PROOF_OF_SALES',
      'PERFORMANCE_REVIEWS',
      'FILM_TV_CREDITS'
    ],
    legalReference: '8 CFR 204.5(h)(3)(x)',
    minimumEvidence: 2
  }
];

/**
 * Get EB-1A criteria configuration
 */
export const EB1A_CONFIG = {
  visaType: 'EB-1A',
  criteria: EB1A_CRITERIA
};
